import type {GridPoint, MapRegistration} from "@/dungeons/model/DungeonGeometry.ts";
import {gridToImage, imageToGrid} from "@/dungeons/geometry/Coordinates.ts";

export type DungeonViewport = {
    center: GridPoint;
    pixelsPerCell: number;
};

type Size = {width: number; height: number};
type ImageSize = {width: number; height: number};

export const fitDungeonViewport = (
    image: ImageSize,
    registration: MapRegistration,
    size: Size,
): DungeonViewport => {
    const topLeft = imageToGrid({x: 0, y: 0}, registration);
    const bottomRight = imageToGrid({x: image.width, y: image.height}, registration);
    const gridWidth = Math.max(Math.abs(bottomRight.x - topLeft.x), Number.EPSILON);
    const gridHeight = Math.max(Math.abs(bottomRight.y - topLeft.y), Number.EPSILON);
    return {
        center: {
            x: (topLeft.x + bottomRight.x) / 2,
            y: (topLeft.y + bottomRight.y) / 2,
        },
        pixelsPerCell: Math.max(0.01, Math.min(size.width / gridWidth, size.height / gridHeight) * 0.92),
    };
};

export const dungeonViewportTransform = (
    viewport: DungeonViewport,
    registration: MapRegistration,
    size: Size,
) => {
    const imageScale = viewport.pixelsPerCell / registration.pixelsPerCell;
    const centerImage = gridToImage(viewport.center, registration);
    return {
        x: size.width / 2 - centerImage.x * imageScale,
        y: size.height / 2 - centerImage.y * imageScale,
        scaleX: imageScale,
        scaleY: imageScale,
    };
};

export const panDungeonViewport = (
    viewport: DungeonViewport,
    delta: GridPoint,
): DungeonViewport => ({
    ...viewport,
    center: {
        x: viewport.center.x - delta.x / viewport.pixelsPerCell,
        y: viewport.center.y - delta.y / viewport.pixelsPerCell,
    },
});

/** Keep the same image pixels under the viewport when the grid registration changes. */
export const rebaseDungeonViewport = (
    viewport: DungeonViewport,
    before: MapRegistration,
    after: MapRegistration,
): DungeonViewport => ({
    center: imageToGrid(gridToImage(viewport.center, before), after),
    pixelsPerCell: viewport.pixelsPerCell * after.pixelsPerCell / before.pixelsPerCell,
});

export const zoomDungeonViewport = (
    viewport: DungeonViewport,
    pointer: GridPoint,
    size: Size,
    factor: number,
): DungeonViewport => {
    const pixelsPerCell = Math.min(2_000, Math.max(2, viewport.pixelsPerCell * factor));
    const gridAtPointer = {
        x: viewport.center.x + (pointer.x - size.width / 2) / viewport.pixelsPerCell,
        y: viewport.center.y + (pointer.y - size.height / 2) / viewport.pixelsPerCell,
    };
    return {
        pixelsPerCell,
        center: {
            x: gridAtPointer.x - (pointer.x - size.width / 2) / pixelsPerCell,
            y: gridAtPointer.y - (pointer.y - size.height / 2) / pixelsPerCell,
        },
    };
};
